import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import Interval from '../uTheory.Notation/interval';
import Note from '../uTheory.Notation/note';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Clef  from '../uTheory.Notation/clef';
import utils from '../utils';
import type { IntervalEarTrainingSkill } from './intervalsETQuestionGenerator';
import { intervalToETSkills } from './intervalsETQuestionGenerator';

/**
 * Values for comparison type:
 * "notes" : (default) compares notes & interval, ignoring octaves
 * "interval" : if the quality & generic are equal, ignoring notes
 * "generic" : if the generic is equal, ignoring notes
 * "ignoreAccidentals" : if the notes are equal, ignoring octaves
 *    and accidentals
 */
export type IntervalComparison =
  | 'notes'
  | 'interval'
  | 'generic'
  | 'ignoreAccidentals'
  | 'enharmonic';

export default class IntervalQuestion {
  interval: Interval;

  note: Note;

  comparisonType: IntervalComparison;

  clef?: Clef;

  harmonic: boolean = false;

  /**
   * An interactive may apply a text string to this for the purposes of instruction
   * display for the question.
   */
  text?: string;

  get earTrainingSkill(): IntervalEarTrainingSkill {
    return intervalToETSkills({ ...this });
  }

  // Public constructor
  constructor(
    interval: Interval | IntervalName,
    note: Note | NoteName,
    options: {
      comparisonType?: IntervalComparison;
      clef?: ClefName | Clef;
      harmonic?: boolean;
    } = {}
  ) {
    this.interval = Interval.stringToInterval(interval);
    this.note = Note.stringToNote(note as NoteName);
    this.comparisonType = options.comparisonType || 'notes';
    if (options.clef) this.clef = Clef.fromStringOrClef(options.clef);
    this.harmonic = !!options.harmonic;
  }

  toString() {
    return `${this.note} ${this.interval}`;
  }

  equals(that: { interval: Interval; note: Note }) {
    switch (this.comparisonType) {
      case 'enharmonic':
        return this.enharmonicallyEqual(that);
      case 'interval':
        return this.intervalEqual(that);
      case 'generic':
        return this.genericEqual(that);
      case 'ignoreAccidentals':
        return this.ignoreAccidentalsEqual(that);
      default:
        return this.notesEqual(that);
    }
  }

  intervalEqual(that: { interval: Interval; note: Note }) {
    return this.interval.equalsIgnoreDirection(that.interval);
  }

  genericEqual(that: { interval: Interval; note: Note }) {
    return this.interval.equalsGenericIgnoreDirection(that.interval);
  }

  enharmonicallyEqual(that: { interval: Interval; note: Note }) {
    const thisIntervalNote = this.note.getNoteAt(this.interval);
    const thatIntervalNote = that.note.getNoteAt(that.interval);
    if (!thisIntervalNote)
      throw new Error(`Impossible interval: ${this.interval} from ${this.note}`);
    if (!thatIntervalNote) return false;

    const thisNotes = new NoteHolder([this.note, thisIntervalNote]);
    const thatNotes = new NoteHolder([that.note, thatIntervalNote]);
    thisNotes.sort();
    thatNotes.sort();
    return (
      thisNotes.notes[0]!.equalsEnharmonically(thatNotes.notes[0]!) &&
      thisNotes.notes[1]!.equalsEnharmonically(thatNotes.notes[1]!)
    );
  }

  notesEqual(that: { interval: Interval; note: Note }) {
    const thisIntervalNote = this.note.getNoteAt(this.interval);
    const thatIntervalNote = that.note.getNoteAt(that.interval);

    if (!thisIntervalNote)
      throw new Error(`Impossible interval: ${this.interval} from ${this.note}`);
    if (!thatIntervalNote) return false;

    if (!thisIntervalNote)
      throw new Error(`Impossible interval: ${this.interval} from ${this.note}`);
    if (!thatIntervalNote) return false;
    const thisNotes = new NoteHolder([this.note, thisIntervalNote]);
    const thatNotes = new NoteHolder([that.note, thatIntervalNote]);
    return thisNotes.equalsIgnoreOctave(thatNotes);
  }

  ignoreAccidentalsEqual(that: { interval: Interval; note: Note }) {
    const thisIntervalNote = this.note.getNoteAtGeneric(this.interval).clone().setAccidental('');
    const thatIntervalNote = that.note.getNoteAtGeneric(that.interval).clone().setAccidental('');

    const thisNotes = new NoteHolder([this.note, thisIntervalNote]);
    const thatNotes = new NoteHolder([that.note, thatIntervalNote]);
    return thisNotes.equalsIgnoreOctave(thatNotes);
  }

  prefersClef(clef?: Clef) {
    const answerNote = this.note.getNoteAtGeneric(this.interval);
    console.log(`Note: ${this.note}, answerNote: ${answerNote}`);
    const possibleNoteClefs = this.note.prefersClef(undefined, 3);
    const possibleAnswerClefs = answerNote.prefersClef(undefined, 2);

    console.log('possibleNoteClefs', possibleNoteClefs);
    console.log('possibleAnswerClefs', possibleAnswerClefs);

    const possibleSingleClefs = utils.intersection(possibleNoteClefs, possibleAnswerClefs);
    console.log(`possible single clefs: ${possibleSingleClefs.join(', ')}`);

    // If we have a prefered clef that works, use it:
    if (clef) {
      if (possibleSingleClefs.includes(clef.toString())) return clef;
      // if it doesn't work, instead use a single clef:
      if (possibleSingleClefs.length) return new Clef(possibleSingleClefs[0]);
    }

    if (possibleSingleClefs.length) {
      const clefString = utils.randomElement(possibleSingleClefs);
      return new Clef(clefString);
    }

    return false;
  }

  fixNoteRange({ grandStaff, clef }: { grandStaff?: boolean; clef?: Clef } = {}) {
    if (grandStaff) return this.fixGrandStaffRange();

    const preferedClef = this.prefersClef(clef);
    if (preferedClef) {
      this.clef = preferedClef;
      return this;
    }

    const possibleNewNotes = [1, 2, 3, 4, 5, 6]
      .map((octave) => {
        const possibleNewNote = this.note.clone();
        possibleNewNote.octave = octave;
        return possibleNewNote;
      })
      .filter((possibleNewNote) => {
        const newQuestion = new IntervalQuestion(this.interval, possibleNewNote, {
          comparisonType: this.comparisonType,
        });
        return !!newQuestion.prefersClef();
      });

    const newNote = utils.randomElement(possibleNewNotes);
    if (newNote) {
      this.note = newNote;
      this.clef = this.prefersClef() as Clef;
      return this;
    }
    throw new Error(`No single clef works for ${this.note} ${this.interval}`);
  }

  fitsGrandStaff() {
    const answerNote = this.note.getNoteAtGeneric(this.interval);
    return !!(
      this.note.prefersClef(undefined, 3).length && answerNote.prefersClef(undefined, 3).length
    );
  }

  fixGrandStaffRange() {
    if (this.fitsGrandStaff()) return this;
    const possibleNewNotes = [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5]
      .map((octaveChange) => {
        const newNote = this.note.clone();
        newNote.octave += octaveChange;
        return newNote;
      })
      .filter((newNote) => {
        const newQuestion = new IntervalQuestion(this.interval, newNote, {
          comparisonType: this.comparisonType,
        });
        return !!newQuestion.fitsGrandStaff();
      });
    if (!possibleNewNotes.length) {
      throw new Error(
        `There is no octave that fits ${this.note.toShortString()} ${
          this.interval
        } on the grand staff.`
      );
    }
    this.note = utils.randomElement(possibleNewNotes);
    return this;
  }
}
